import type { IMetadata } from '@adloid-technologies/discover-sdk';

/**
 * GMC Hummer EV3x (linkId 2382, project 59) — SUV and Pickup body styles.
 *
 * Converted from the 1.0 repo's `src/metadata/data/metadata-2382.ts`
 * (`appData.visualiser.models[0]`, array-shaped) into the 2.0 object-keyed
 * `IMetadata` shape.
 *
 * 1.0 reuses one configurationId across BOTH body styles for nearly every
 * entity (colours, features, accessories, interactions, hotspots — 6/6, 20/21,
 * 6/6, 5/5, 2/2 respectively), but the content underneath differs: separate
 * SUV_* / SUT_* swatch and feature stills per body style. Where the two
 * variants' copies fingerprint identically they collapse to one entry naming
 * both in `relevantVariants`; where they differ (nearly everything with a
 * real image) the SUV copy keeps the bare id and Pickup's gets a `-pickup`
 * suffix. Same mechanism as Groove (2386), just triggered on far more ids.
 *
 * `hotspots` is model-level, not per-variant (confirmed against the SDK's
 * Record<ICameraNames, IHotspot> type and against Silverado's single hotspots
 * block across 18 variants) — built from the default (SUV) variant's
 * 3dhotspots; Pickup's identical rows are redundant under the schema.
 *
 * Generated — see the conversion notes in ui-metadata.ts.
 */
export const metadata: IMetadata = {
  models: {
    Hummer: {
      id: 'Hummer',
      lightingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      postProcessingIdentifier: {
        interior: 'dome',
        exterior: 'dome',
      },
      backgroundIdentifer: 'dome',
      variants: {
        suv: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6213',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6214',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6193',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'variants_parts',
              sku: '6197',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '6226',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
        pickup: {
          defaultSkus: [
            {
              id: 'common_exterior',
              sku: '6213',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'interior-color',
              sku: '6214',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'exterior-color',
              sku: '6193',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'variants_parts',
              sku: '6202',
              projectId: '59',
              position: [0, 0, 0],
            },
            {
              id: 'dome',
              sku: '6225',
              projectId: '59',
              position: [0, 0, 0],
              maxResolution: {
                ios: '2K',
                android: '4K',
              },
            },
          ],
          interiorColorIdentifier: 'interior-color',
          exteriorColorIdentifier: 'exterior-color',
        },
      },
      backgrounds: {
        g0ZA968_RpUd: {
          isDefault: false,
          sku: '5526',
          project: '59',
        },
        g0ZA968_RpUe: {
          isDefault: true,
          sku: '5524',
          project: '59',
        },
      },
      ambientLighting: {
        ambientColors: '',
        ambientLightings: {},
        relevantVariants: [],
      },
      colors: {
        exterior: {
          g946ignut6zm: {
            sku: '6193',
            project: '59',
            relevantVariants: [
              'suv',
            ],
          },
          'g946ignut6zm-pickup': {
            sku: '6193',
            project: '59',
            relevantVariants: [
              'pickup',
            ],
          },
          '5zwfz8t9v1ci': {
            sku: '6203',
            project: '59',
            relevantVariants: [
              'suv',
            ],
          },
          '5zwfz8t9v1ci-pickup': {
            sku: '6203',
            project: '59',
            relevantVariants: [
              'pickup',
            ],
          },
          '3bcwcix9d6ow': {
            sku: '6200',
            project: '59',
            relevantVariants: [
              'suv',
            ],
          },
          '3bcwcix9d6ow-pickup': {
            sku: '6200',
            project: '59',
            relevantVariants: [
              'pickup',
            ],
          },
          ekrnov29ywox: {
            sku: '6210',
            project: '59',
            relevantVariants: [
              'suv',
            ],
          },
          'ekrnov29ywox-pickup': {
            sku: '6210',
            project: '59',
            relevantVariants: [
              'pickup',
            ],
          },
          t97r2gqldscc: {
            sku: '6208',
            project: '59',
            relevantVariants: [
              'suv',
            ],
          },
          't97r2gqldscc-pickup': {
            sku: '6208',
            project: '59',
            relevantVariants: [
              'pickup',
            ],
          },
          e12d2rs52b1b: {
            sku: '6199',
            project: '59',
            relevantVariants: [
              'suv',
            ],
          },
          'e12d2rs52b1b-pickup': {
            sku: '6199',
            project: '59',
            relevantVariants: [
              'pickup',
            ],
          },
          e12d2rrrs52b1b: {
            sku: '6198',
            project: '59',
            relevantVariants: [
              'suv',
            ],
          },
          'e12d2rrrs52b1b-pickup': {
            sku: '6198',
            project: '59',
            relevantVariants: [
              'pickup',
            ],
          },
        },
        interior: {
          d4xeirch0we2: {
            sku: '6214',
            project: '59',
            relevantVariants: [
              'suv',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['58v90smxqqsfey'],
          },
          'd4xeirch0we2-pickup': {
            sku: '6214',
            project: '59',
            relevantVariants: [
              'pickup',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['58v90smxqqsfey-pickup'],
          },
          '5vktfJxuscn42': {
            sku: '6212',
            project: '59',
            relevantVariants: [
              'suv',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['58v90smxqqsfey'],
          },
          '5vktfJxuscn42-pickup': {
            sku: '6212',
            project: '59',
            relevantVariants: [
              'pickup',
            ],
            notCompatibleExteriorColors: [],
            accessoriesToReload: ['58v90smxqqsfey-pickup'],
          },
        },
      },
      features: {
        '3h3fjg5zh489': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'SHADER_Grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        '3h3fjg5zh489-pickup': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'SHADER_Grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Grille',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ngs1smpm2k89: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'SHADER_Roof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Roof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'ngs1smpm2k89-pickup': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'SHADER_Roof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Roof',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        slfnh3hjd6s8: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'Marker_Lights',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Marker_Lights',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'slfnh3hjd6s8-pickup': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'Marker_Lights',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Marker_Lights',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        zeq7pry7mouo: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'ETRUNK',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_ETRUNK',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'zeq7pry7mouo-pickup': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'ETRUNK',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_ETRUNK',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        marg8otdccyk: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'Alloys',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Alloys',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'marg8otdccyk-pickup': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'Alloys',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Alloys',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        marg8ffotdccyk: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'Brake_Lights_SUV',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Brake_Lights_SUV',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            ygmre5317vts: { add: false, snapToLastFrame: true },
          },
        },
        'marg8ffotdccyk-pickup': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'Brake_Lights_SUT',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Brake_Lights_SUT',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            ygmre5317vts: { add: false, snapToLastFrame: true },
          },
        },
        mporg8ffotdccyk: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'Adaptive_Air_Ride',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'mporg8ffotdccyk-pickup': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'Adaptive_Air_Ride',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        marg8otdkoccyk: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'Taillights_SUV',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Taillights_SUV',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'marg8otdkoccyk-pickup': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'Taillights_SUT',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Taillights_SUT',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        marg8otddkoccyk: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'Charging_Inlet_SUV',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Charging_Inlet_SUV',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'marg8otddkoccyk-pickup': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'Charging_Inlet_SUT',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Charging_Inlet_SUT',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        kiarg8otddkoccyk: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'Rear_Glass_SUV',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Rear_Glass_SUV',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            ygmre5317vts: { add: false, snapToLastFrame: true },
          },
        },
        'kiarg8otddkoccyk-pickup': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'Rear_Glass_SUT',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Rear_Glass_SUT',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            ygmre5317vts: { add: false, snapToLastFrame: true },
          },
        },
        kiiarg9otddkoccyk: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'Power_Swing_Gate',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Power_Swing_Gate',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            ygmre5317vts: { add: false, snapToLastFrame: true },
          },
        },
        'kiiarg9otddkoccyk-pickup': {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'Approach_Angle',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Approach_Angle',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        oxfitdgg14gk: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'SHADER_Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'oxfitdgg14gk-pickup': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'SHADER_Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Infoscreen',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        ppj9rls95crn: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'SHADER_Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'ppj9rls95crn-pickup': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'SHADER_Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'Speedometer',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        okumo92efxcj: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'SHADER_IRVM',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'IRVM',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'okumo92efxcj-pickup': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'SHADER_IRVM',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
            {
              message: 'IRVM',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        z5e0bv2uqipu: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'Air_Vents',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Air_Vents',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'z5e0bv2uqipu-pickup': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'Air_Vents',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Air_Vents',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        z5ee0pbv2uqipu: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'ETS',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_ETS',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'z5ee0pbv2uqipu-pickup': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'ETS',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_ETS',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        p5ee0pbv2uqipu: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'Driver_Mode',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Driver_Mode',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'p5ee0pbv2uqipu-pickup': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'Driver_Mode',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Driver_Mode',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        p7ee0pbv2uqipu: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'Bose_Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Bose_Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            a9jk90o1h6fp: { add: false, snapToLastFrame: true },
          },
        },
        'p7ee0pbv2uqipu-pickup': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'Bose_Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Bose_Speaker',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {
            a9jk90o1h6fp: { add: false, snapToLastFrame: true },
          },
        },
        p7pe0pbv2dqipu: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'Seat_Storage',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Seat_Storage_SUV',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'p7pe0pbv2dqipu-pickup': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'Seat_Storage',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Seat_Storage_SUT',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        p7pe0pblv2dqipu: {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'suv',
          ],
          messages: [
            {
              message: 'Rear Seat',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Rear Seat',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        'p7pe0pblv2dqipu-pickup': {
          type: 'graphic',
          view: 'interior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'Rear Seat',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Rear Seat',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
        sroplfnh3hjd6s8: {
          type: 'graphic',
          view: 'exterior',
          relevantVariants: [
            'pickup',
          ],
          messages: [
            {
              message: 'Side_Steps',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: true,
              cascade: false,
            },
            {
              message: 'SHADER_Side_Steps',
              identifiers: [
                'common_exterior',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          mesagesOnApply: [],
          messagesOnRemove: [],
          affectedInteractionOnApply: {},
        },
      },
      accessories: {
        '8ylilfy10w5s': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'SUV_EXT_Variant',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'suv',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '8ylilfy10w5s-pickup': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'SUT_EXT_Variant',
              identifiers: [
                'exterior-color',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'pickup',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        whv4p5vjna3p: {
          view: 'exterior',
          groupId: 'pickup-g0',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'Opaque_Roof',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'suv',
            'pickup',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        gzf6oq39rp8k: {
          view: 'exterior',
          groupId: 'suv-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Extreme_Off_Road_Package_SUV',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'suv',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'gzf6oq39rp8k-pickup': {
          view: 'exterior',
          groupId: 'pickup-g1',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'WO_Extreme_Off_Road_Package_SUT',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'pickup',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        mbxtmb9ivqay: {
          view: 'exterior',
          groupId: 'suv-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Infinity_Roof',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Roof',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'suv',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        'mbxtmb9ivqay-pickup': {
          view: 'exterior',
          groupId: 'pickup-g0',
          display: true,
          isDefault: false,
          applyMessages: [
            {
              message: 'Infinity_Roof',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [
            {
              message: 'CAMERA_ROOF',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          shaderMessages: [
            {
              message: 'SHADER_Roof',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'pickup',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '58vsmxqqsfey': {
          view: 'exterior',
          groupId: 'suv-g1',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Extreme_Off_Road_Package_SUV',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {
            ygmre5317vts: {
              add: false,
              snapToLastFrame: true,
            },
          },
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'suv',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '58vsmxqqsfey-pickup': {
          view: 'exterior',
          groupId: 'pickup-g1',
          display: false,
          isDefault: false,
          applyMessages: [
            {
              message: 'W_Extreme_Off_Road_Package_SUT',
              identifiers: [
                'common_exterior',
              ],
              condition: '',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'pickup',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '58v90smxqqsfey': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'SUV_Lunar_Horizon',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6214) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'SUV_Lunar_Shadow',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6212) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'suv',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
        '58v90smxqqsfey-pickup': {
          view: 'exterior',
          groupId: '',
          display: false,
          isDefault: true,
          applyMessages: [
            {
              message: 'SUT_Lunar_Horizon',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6214) is (interior-color)',
              isDefault: true,
              isStop: false,
            },
            {
              message: 'SUT_Lunar_Shadow',
              identifiers: [
                'common_exterior',
              ],
              condition: '(6212) is (interior-color)',
              isDefault: false,
              isStop: false,
            },
          ],
          cameraMessages: [],
          shaderMessages: [],
          affectedInteractionOnApply: {},
          mesagesOnApply: [],
          messagesOnRemove: [],
          dependencyAccessories: [],
          relevantVariants: [
            'pickup',
          ],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
        },
      },
      packages: {
        x7vtyriufcgr0: {
          groupId: 'suv-pkg0',
          accessoryIds: [
            '58vsmxqqsfey',
          ],
          dependencyPackages: [],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
          relevantVariants: [
            'suv',
          ],
        },
        x7vtyripoufcgr0: {
          groupId: 'pickup-pkg0',
          accessoryIds: [
            '58vsmxqqsfey-pickup',
          ],
          dependencyPackages: [],
          notAvailableForColor: {
            interior: [],
            exterior: [],
          },
          relevantVariants: [
            'pickup',
          ],
        },
      },
      interactions: {
        a9jk90o1h6fp: {
          type: 'basic',
          view: [
            'exterior',
            'interior',
          ],
          startMessages: [
            {
              message: 'DOORS_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'DOORS_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.0032,
                beta: 1.3201,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {
                alpha: 3.5141,
                beta: 1.4083,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {
                alpha: 3.4327,
                beta: 1.3211,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {
                alpha: 5.0385,
                beta: 1.448,
                radius: 0.1,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
          },
          relevantVariants: [
            'suv',
            'pickup',
          ],
        },
        ygmre5317vts: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILGATE_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILGATE_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'suv',
            'pickup',
          ],
        },
        q5wycu9jmjsc: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'HEADLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.4729,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'suv',
            'pickup',
          ],
        },
        ehdd0bdxbb0i: {
          type: 'basic',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'ETRUNK_OPEN',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          reverseMessages: [
            {
              message: 'ETRUNK_CLOSE',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: true,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 1.573,
                beta: 1.473,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'suv',
            'pickup',
          ],
        },
        yi26t2yj1ipi: {
          type: 'basic-light',
          view: [
            'exterior',
          ],
          startMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          reverseMessages: [
            {
              message: 'TAILLIGHT_GLOW',
              identifiers: [
                'exterior-color',
                'common_exterior',
                'common_interior',
                'interior-color',
                'variants_parts',
                'bed_variant',
                'optional',
              ],
              waitForFulfill: false,
              cascade: false,
            },
          ],
          camera: {
            exteriorCamera: {
              type: 'exterior',
              messages: {
                alpha: 4.71,
                beta: 1.3951,
                radius: 10,
                isBlend: true,
                blendInSeconds: 1.5,
              },
            },
            interiorCamera: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_1: {
              type: 'interior',
              messages: {},
            },
            interiorCamera_2: {
              type: 'interior',
              messages: {},
            },
          },
          relevantVariants: [
            'suv',
            'pickup',
          ],
        },
      },
      hotspots: {
        exteriorCamera: {
          type: 'exterior',
          messages: {
            fov: 0.4,
            nearplane: 0.1,
            alpha: 2.02,
            beta: 1.47,
            radius: 10,
            lowerAlphaLimit: null,
            upperAlphaLimit: null,
            lowerBetaLimit: 0.1,
            upperBetaLimit: 1.6,
            lowerRadiusLimit: 8,
            upperRadiusLimit: 11,
            target: {
              x: 0,
              y: 0.65,
              z: 0,
            },
            isFovZoomModeOn: false,
          },
        },
        interiorCamera: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA',
            identifiers: [
              'common_exterior',
            ],
          },
        },
        interiorCamera_1: {
          type: 'interior',
          messages: {
            message: 'INT_DEFAULT_CAMERA_2',
            identifiers: [
              'common_exterior',
            ],
          },
        },
        interiorCamera_2: {
          type: 'interior',
          messages: {},
        },
      },
      usdzPathWithVariable: '',
    },
  },
};
